import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";
import { useT } from "../lib/i18n.jsx";
import { useToast } from "../components/Toast.jsx";
import { IconCheck, IconX } from "../components/icons.jsx";

const ALL_ROLES = ["superadmin", "admin", "moderator", "user"];

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const { t } = useT();
  const toast = useToast();
  const [users, setUsers] = useState(null);
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [warnTarget, setWarnTarget] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [message, setMessage] = useState(null);

  const isSuperAdmin = me?.role === "superadmin";
  const isAdmin = me?.role === "admin" || isSuperAdmin;
  const isMod = me?.role === "moderator" || isAdmin;

  async function load() {
    const r = await api("/admin/users");
    setUsers(r.users);
    if (isAdmin) {
      api("/admin/plans").then((p) => setPlans(p.plans)).catch(() => {});
    }
  }

  useEffect(() => {
    load().catch((err) => toast.error(err.message));
  }, []);

  async function save(userId, patch) {
    try {
      await api(`/admin/users/${userId}`, { method: "PUT", body: patch });
      setEditing(null);
      toast.success(t("admin_users_updated"));
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function warn(userId, reason) {
    try {
      await api(`/admin/users/${userId}/warn`, { method: "POST", body: { reason } });
      setWarnTarget(null);
      toast.success(t("admin_users_warned"));
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function suspend(userId, reason) {
    try {
      await api(`/admin/users/${userId}/suspend`, { method: "POST", body: { reason } });
      setSuspendTarget(null);
      toast.success(t("admin_users_suspended"));
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function unsuspend(userId) {
    try {
      await api(`/admin/users/${userId}/unsuspend`, { method: "POST" });
      toast.success(t("admin_users_unsuspended"));
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  if (!users) return <div className="loading">{t("common_loading")}</div>;

  return (
    <>
      <div className="ph">
        <div className="ph-title">
          <div className="eyebrow">Administration</div>
          <h1 className="h1">{t("admin_users_title")}</h1>
        </div>
        <div className="ph-meta">{users.length} {t("admin_users_accounts")}</div>
      </div>

      {message?.ok && null}
      {message?.error && null}

      <div className="section">
        <div className="section-body" style={{ padding: 0 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>{t("admin_users_plan")}</th>
                <th>{t("admin_users_devices")}</th>
                <th>{t("admin_users_effective_slots")}</th>
                <th>{t("admin_users_last_login")}</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={u.is_suspended ? { opacity: 0.5 } : {}}>
                  <td><span className="mono">{u.email}</span></td>
                  <td>{u.display_name}</td>
                  <td>
                    <span className={`chip ${u.role === "superadmin" ? "err" : u.role === "admin" ? "acc" : u.role === "moderator" ? "warn" : ""}`}>
                      {t(`role_${u.role}`)}
                    </span>
                  </td>
                  <td>{u.plan_name || <span className="dim">free</span>}</td>
                  <td>{u.devices_used} / {u.effective_device_slots}</td>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {u.effective_device_slots}d · {u.effective_viewer_slots}v
                  </td>
                  <td className="mono" style={{ fontSize: 11, color: "var(--text-dim)" }}>
                    {u.last_login_at
                      ? new Date(u.last_login_at).toISOString().slice(0, 16).replace("T", " ")
                      : t("admin_users_never")}
                  </td>
                  <td>
                    {u.is_suspended
                      ? <span className="chip err">suspended</span>
                      : <span className="chip ok">{t("common_active")}</span>}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {isAdmin && <button className="btn ghost" onClick={() => setEditing(u)}>{t("common_edit")}</button>}
                      {isMod && <button className="btn ghost" onClick={() => setWarnTarget(u)}>{t("admin_users_warn")}</button>}
                      {isMod && (u.is_suspended
                        ? <button className="btn ghost" onClick={() => unsuspend(u.id)}>{t("admin_users_unsuspend")}</button>
                        : <button className="btn ghost danger" onClick={() => setSuspendTarget(u)}>{t("admin_users_suspend")}</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && isAdmin && (
        <Modal onClose={() => setEditing(null)}>
          <div className="eyebrow">{t("common_edit")}</div>
          <h2 style={{ fontSize: 20, margin: "8px 0 4px", fontWeight: 600 }}>{editing.display_name}</h2>
          <div className="mono dim" style={{ fontSize: 11, marginBottom: 20 }}>{editing.email}</div>
          <EditForm user={editing} plans={plans} isSuperAdmin={isSuperAdmin} t={t}
            onClose={() => setEditing(null)} onSave={(p) => save(editing.id, p)} />
        </Modal>
      )}
      {warnTarget && (
        <Modal onClose={() => setWarnTarget(null)}>
          <ReasonForm title={`${t("admin_users_warn")}: ${warnTarget.display_name}`}
            placeholder={t("admin_users_warn_placeholder")} t={t}
            onClose={() => setWarnTarget(null)} onSubmit={(r) => warn(warnTarget.id, r)} />
        </Modal>
      )}
      {suspendTarget && (
        <Modal onClose={() => setSuspendTarget(null)}>
          <ReasonForm title={`${t("admin_users_suspend")}: ${suspendTarget.display_name}`}
            placeholder={t("admin_users_suspend_placeholder")} danger t={t}
            onClose={() => setSuspendTarget(null)} onSubmit={(r) => suspend(suspendTarget.id, r)} />
        </Modal>
      )}
    </>
  );
}

function EditForm({ user, plans, isSuperAdmin, t, onClose, onSave }) {
  const [planId, setPlanId] = useState(user.current_plan_id ?? "");
  const [extraDev, setExtraDev] = useState(user.extra_device_slots || 0);
  const [extraView, setExtraView] = useState(user.extra_viewer_slots || 0);
  const [role, setRole] = useState(user.role);
  return (
    <>
      <div className="field">
        <label>{t("admin_users_plan")}</label>
        <select value={planId} onChange={(e) => setPlanId(e.target.value)}>
          <option value="">— Free —</option>
          {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div className="field"><label>+Device slots</label>
          <input type="number" min={0} value={extraDev} onChange={(e) => setExtraDev(Number(e.target.value))} /></div>
        <div className="field"><label>+Viewer slots</label>
          <input type="number" min={0} value={extraView} onChange={(e) => setExtraView(Number(e.target.value))} /></div>
      </div>
      {isSuperAdmin && (
        <div className="field"><label>Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            {ALL_ROLES.map((r) => <option key={r} value={r}>{t(`role_${r}`)}</option>)}
          </select>
          <span className="hint">Only superadmins can change roles.</span>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
        <button className="btn ghost" onClick={onClose} style={{ flex: 1 }}><IconX size={12} /> {t("common_cancel")}</button>
        <button className="btn primary" style={{ flex: 1 }} onClick={() => onSave({
          current_plan_id: planId === "" ? null : Number(planId),
          extra_device_slots: extraDev, extra_viewer_slots: extraView,
          ...(isSuperAdmin ? { role } : {}),
        })}><IconCheck size={12} /> {t("common_save")}</button>
      </div>
    </>
  );
}

function ReasonForm({ title, placeholder, danger, t, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  return (
    <>
      <div className="eyebrow">{title}</div>
      <div className="field" style={{ marginTop: 16 }}>
        <label>Reason</label>
        <input type="text" placeholder={placeholder} value={reason}
          onChange={(e) => setReason(e.target.value)} autoFocus />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button className="btn ghost" onClick={onClose} style={{ flex: 1 }}>{t("common_cancel")}</button>
        <button className={"btn " + (danger ? "danger" : "primary")} style={{ flex: 1 }}
          onClick={() => onSubmit(reason)}>Confirm</button>
      </div>
    </>
  );
}

function Modal({ children, onClose }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      backdropFilter: "blur(8px)", display: "flex", alignItems: "center",
      justifyContent: "center", zIndex: 100, padding: 20,
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--bg-elev)", border: "1px solid var(--line-strong)",
        borderRadius: "var(--r-lg)", padding: 28, width: "100%", maxWidth: 480,
        boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
      }}>
        {children}
      </div>
    </div>
  );
}
